import { ContentHubArticle } from '@propertypal/shared/src/types/strapi/overrides';
import { findRichText, getArticleImage } from '@propertypal/shared/src/utils/content-hub';
import platform from '@propertypal/shared/src/utils/platform';
import dayjs from 'dayjs';
import Link from 'next/link';
import React from 'react';
import Chevron from '../icons/Chevron';
import { Box } from '../layout';
import { NewsBox, NewsContent, NewsImage } from './ContentHubArticleBox.style';
import { Title, ViewMore } from './ContentHubCarousel.style';
import {
  ArticleGrid,
  ArticleItem,
  ContentHubGridContainer,
  ContentHubGridContent,
  ContentHubGridSmallText,
  DoorIllustration,
  IllustrationContainer,
  MobileViewMore,
} from './ContentHubGrid.style';
import ContentHubNewsLetter from './ContentHubNewsLetter';
import { ContentHubContent } from './ContentHubPageContainer.style';
import 'swiper/css';
import 'swiper/css/scrollbar';

interface ContentHubGridProps {
  articles?: ContentHubArticle[];
  title: string;
  viewMore?: string;
  viewMoreTitle?: string;
  viewMoreWidth?: number;
  illustration?: boolean;
  maxW?: number;
  smallText?: string;
}

const ContentHubGrid: React.FC<ContentHubGridProps> = ({
  articles,
  title,
  viewMore,
  illustration,
  viewMoreTitle = 'View more',
  viewMoreWidth,
  maxW,
  smallText,
}) => {
  if (articles && articles.length > 0) {
    return (
      <ContentHubGridContainer data-testid="articles-grid" illustration={illustration}>
        <ContentHubContent $maxW={maxW}>
          <Title>{title}</Title>

          {viewMore && (
            <Link href={viewMore} prefetch={false} className="view-more">
              <ViewMore $w={viewMoreWidth}>{viewMoreTitle}</ViewMore>
            </Link>
          )}
        </ContentHubContent>
        <ContentHubGridContent $maxW={maxW}>
          <ArticleGrid>
            {articles
              .filter((a) => !a?.attributes?.hidden)
              .sort((a, b) => (dayjs(b.attributes.publishedAt).isBefore(dayjs(a.attributes.publishedAt)) ? -1 : 1))
              .slice(0, 4)
              .map((article) => (
                <ArticleItem key={article.id} data-testid="articles-grid-item">
                  <NewsBox
                    href={`${platform.select(
                      // eslint-disable-next-line max-len
                      `/${article.attributes.subCategories?.data[0].attributes.articleCategory}/${article.attributes.subCategories?.data[0].attributes.slug}`,
                      '/news',
                    )}/${article.attributes.slug}`}
                  >
                    <NewsImage image={getArticleImage(article)} />

                    <NewsContent>
                      <Box gap={15} display="flex" flexDirection="column">
                        <h3>{article.attributes.title}</h3>

                        <p className="article-text">
                          {article.attributes.excerpt || findRichText(article.attributes.content)}
                        </p>
                      </Box>

                      <p>
                        Read more <Chevron size={11} />
                      </p>
                    </NewsContent>
                  </NewsBox>
                </ArticleItem>
              ))}
          </ArticleGrid>
          {viewMore && (
            <MobileViewMore>
              <Link href={viewMore} prefetch={false}>
                <ViewMore>View more</ViewMore>
              </Link>
            </MobileViewMore>
          )}
        </ContentHubGridContent>
        {smallText && (
          <ContentHubGridSmallText data-testid="articles-grid-small-text">{smallText}</ContentHubGridSmallText>
        )}
        {illustration && (
          <>
            <ContentHubGridContent>
              <ContentHubNewsLetter category page="Content Hub Landing Page" location="Subscribe section" />
            </ContentHubGridContent>
            <IllustrationContainer data-testid="articles-grid-illustration">
              <DoorIllustration />
            </IllustrationContainer>
          </>
        )}
      </ContentHubGridContainer>
    );
  }

  return null;
};

export default ContentHubGrid;
